/**
 * The practice-forms TEMPLATE and VERSION API — `tasks/patient-forms.md` §13.2 rows 1-11.
 *
 * Scope is deliberately narrow: forms, versions, and appointment-type triggers. `PatientFormRequest`
 * (§13.2 rows 12-16) belongs to the patient-file panel and is NOT here — a service that reaches both
 * would make the settings tab a dependency of the patient file.
 *
 * ── THE ONE RULE THIS FILE ENFORCES FOR ITS CALLERS ──────────────────────────────────────────
 *
 * `schema` NEVER travels on a create or an update. §14.1 decision 4: the server refuses such a body
 * with 400 `SCHEMA_IS_VERSIONED` rather than ignoring the key — deliberately, because silently
 * dropping it ships a Save button that appears to work and changes nothing. Rather than trusting
 * every caller to remember that, `createForm`/`updateForm` build their bodies FIELD BY FIELD from a
 * typed metadata shape, so a `schema` key cannot ride along even if a caller hands one over. The one
 * road a schema takes is `publishVersion`.
 *
 * The Authorization header is attached per call from `localStorage`, matching every other service in
 * this directory (`survey-template.service.ts`, `appointment-type.service.ts`).
 */

import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { FormSchema } from '../../shared/models/custom-form.model';
import { GroupService } from './group.service';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * DTOs — mirroring `customFormController.js`'s `toFormDto` / `toVersionDto` / `toLinkDto`
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export type CustomFormStatus = 'draft' | 'published' | 'archived';
export type CustomFormSendOn = 'booking' | 'reminder';

export interface CustomFormDto {
  id: string;
  groupId: string;
  name: string;
  description: string | null;
  category: string | null;
  currentVersionId: string | null;
  status: CustomFormStatus;
  sendWithInvite: boolean;
  allowManualSend: boolean;
  requiresSignature: boolean;
  /** Patient app: a parent or guardian may complete it for a 12 to 17 year old (off: the young person does). */
  guardianMayComplete?: boolean;
  expiryDays: number | null;
  isAiGenerated: boolean;
  sourceFileUrl: string | null;
  createdByUserId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * `schema` is OPTIONAL on purpose, and its absence is meaningful rather than incidental.
 *
 * §14.1 decision 2: the history list omits it — ten encrypted questionnaires to draw a list of
 * dates. `GET /custom-form-versions/:versionId` is the only place a schema arrives. A caller that
 * reads `version.schema` off a history row gets `undefined`, and this optionality is what makes the
 * compiler say so.
 */
export interface CustomFormVersionDto {
  id: string;
  customFormId: string;
  groupId: string;
  versionNumber: number;
  publishedByUserId: string | null;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
  schema?: FormSchema | null;
}

/** `GET /custom-forms/:id` — the form plus the version it currently publishes, schema included. */
export interface CustomFormDetailDto extends CustomFormDto {
  currentVersion: CustomFormVersionDto | null;
}

export interface CustomFormLinkDto {
  id: string;
  groupId: string;
  customFormId: string;
  appointmentTypeId: string;
  sendOn: CustomFormSendOn;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  appointmentType: {
    id: string;
    type: string;
    backgroundColor: string | null;
    textColor: string | null;
    isProcedure: boolean;
    active: boolean;
  } | null;
}

/**
 * The EDITABLE surface, and nothing else.
 *
 * This mirrors `applyFormMetadata` in the controller exactly. `groupId`, `currentVersionId`,
 * `createdByUserId`, `id` and `schema` are all absent by construction — the controller refuses each
 * of them by name (§14.1 decision 4), so a body that cannot express them is a request that cannot be
 * refused for that reason.
 */
export interface CustomFormMetadata {
  name?: string;
  description?: string | null;
  category?: string | null;
  status?: CustomFormStatus;
  sendWithInvite?: boolean;
  allowManualSend?: boolean;
  requiresSignature?: boolean;
  guardianMayComplete?: boolean;
  expiryDays?: number | null;
  active?: boolean;
}

/* The server's envelopes. */

export interface CustomFormListResponse { success: boolean; data: CustomFormDto[]; total: number; }
export interface CustomFormResponse { success: boolean; data: CustomFormDto; }
export interface CustomFormDetailResponse { success: boolean; data: CustomFormDetailDto; }
export interface CustomFormVersionResponse { success: boolean; data: CustomFormVersionDto; }

export interface CustomFormVersionListResponse {
  success: boolean;
  data: CustomFormVersionDto[];
  total: number;
  /** So the history can mark which row is live without a second read. */
  currentVersionId: string | null;
}

export interface PublishVersionResponse {
  success: boolean;
  data: { version: CustomFormVersionDto; form: CustomFormDto };
}

export interface CustomFormLinkListResponse {
  success: boolean;
  data: CustomFormLinkDto[];
  total: number;
}

/**
 * §14.1 decision 8. 201 = created, 200 = reactivated-or-already-linked, and the two booleans say
 * which — deliberately NOT the survey controller's 400 "already linked". A paused row still occupies
 * the unique slot, so "already linked" is a normal outcome of pressing Link, not an error.
 */
export interface CustomFormLinkResponse {
  success: boolean;
  created: boolean;
  reactivated: boolean;
  message: string;
  data: CustomFormLinkDto;
}

export interface CustomFormUnlinkResponse {
  success: boolean;
  message: string;
  data: { customFormId: string; appointmentTypeId: string; removed: number };
}

export interface CustomFormDeleteResponse {
  success: boolean;
  message: string;
  data: { id: string };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * AI IMPORT FROM A PDF — §16.1 / §16.2 / §16.3
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export type CustomFormImportStatus = 'pending' | 'processing' | 'completed' | 'failed';

/**
 * One row of `CustomFormImports` (§16.1) — an async job whose result the client comes back for.
 *
 * `schema` is a **draft**. It is not a version, it has never been published, and nothing in this
 * client may publish it: the only road to a version is `publishVersion`, pressed by a person. §16.2
 * rule 1 is the whole feature, and this DTO is where it would be lost first.
 */
export interface CustomFormImportDto {
  id: string;
  groupId: string;
  customFormId: string;
  requestedByUserId: string | null;
  status: CustomFormImportStatus;
  /** §16.2 rule 4 — the original, so a reviewer can compare the draft against the document. */
  sourceFileUrl: string | null;
  sourceFileName: string;
  pageCount: number | null;
  /** Why it failed, in the server's own words. Shown verbatim; never replaced with "try again". */
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;

  /** Only on `GET /custom-form-imports/:importId`, and only once the extraction has finished. */
  schema?: FormSchema | null;

  /**
   * On the LIST rows, which carry no schema (ten encrypted questionnaires to draw a list of file
   * names). It is `true` on a `failed` row too, and that is not a contradiction — see
   * `hasRepairableSchema`.
   */
  hasSchema?: boolean;

  /**
   * §16.2 rule 2 — `validateSchema`'s verdict on the extraction, stored beside the draft so the
   * practice can repair it rather than pay for the extraction twice.
   *
   * ── A GAP IN §16.3, NORMALISED HERE RATHER THAN GUESSED AT EACH CALL SITE ───────────────────
   * §16.3 says the errors are stored (rule 2) but names no field for them on the response, and the
   * endpoint as built does not send one: it folds them into `errorMessage` as a bullet list and
   * marks the row `failed`. Both of those are read by `CustomFormService.importSchemaErrors`, which
   * is the ONE place that knows how the errors arrive — a component that read a field directly
   * would render an empty list and tell a practice their extraction was clean.
   */
  schemaErrors?: string[] | null;
  validationErrors?: string[] | null;
}

export interface CustomFormImportResponse { success: boolean; data: CustomFormImportDto; }

export interface CustomFormImportListResponse {
  success: boolean;
  data: CustomFormImportDto[];
  total: number;
}

/** Every refusal in `customFormController.js` has this shape. */
export interface CustomFormApiError {
  success: false;
  error: string;
  message: string;
  details?: {
    /** `SCHEMA_INVALID` — `validateSchema`'s messages, one per problem. */
    errors?: string[];
    /** `CUSTOM_FORM_HAS_OPEN_REQUESTS` — §14.1 / the delete handler. */
    openRequestCount?: number;
    byStatus?: { [status: string]: number };
    [key: string]: any;
  };
}

@Injectable({ providedIn: 'root' })
export class CustomFormService {

  private apiUrl = 'api';

  /**
   * The practice a create lands in.
   *
   * §14.1 decision 6: `body.groupId` is honoured only when the caller is a member of it, and a
   * caller in several practices who names none gets 400 `GROUP_ID_REQUIRED` rather than a guess. So
   * every create sends the selected practice explicitly — a single-practice user is unaffected, and
   * a multi-practice user's form lands in the practice their settings page is actually showing.
   */
  selectedGroupId: string | null = null;

  constructor(private http: HttpClient, private groupService: GroupService) {
    // The subscription pattern the other core services use (`appointment-type.service.ts`). No
    // takeUntil: this service is root-provided and lives as long as the app does.
    this.groupService.selectedGroup$.subscribe((group: any) => {
      this.selectedGroupId = group && group.id ? group.id : null;
    });
  }

  /* ── Templates ──────────────────────────────────────────────────────────────────────────── */

  listForms(filters: { status?: CustomFormStatus; category?: string; active?: boolean; groupId?: string } = {})
    : Observable<CustomFormListResponse> {
    let params = new HttpParams();
    if (filters.status) params = params.set('status', filters.status);
    if (filters.category) params = params.set('category', filters.category);
    if (filters.active !== undefined) params = params.set('active', String(filters.active));

    // §14.1 decision 7: `?groupId=` intersects with membership and can never widen it, so sending
    // the selected practice is a narrowing filter, never a privilege claim.
    const groupId = filters.groupId !== undefined ? filters.groupId : this.selectedGroupId;
    if (groupId) params = params.set('groupId', groupId);

    return this.http.get<CustomFormListResponse>(`${this.apiUrl}/custom-forms`, {
      headers: this.authHeaders(),
      params,
    });
  }

  getForm(id: string): Observable<CustomFormDetailResponse> {
    return this.http.get<CustomFormDetailResponse>(`${this.apiUrl}/custom-forms/${id}`, {
      headers: this.authHeaders(),
    });
  }

  /** Always lands in `draft` — §14.1 decision 5 says a `status` in the body is ignored. */
  createForm(metadata: CustomFormMetadata & { name: string }): Observable<CustomFormResponse> {
    const body: any = this.metadataBody(metadata);
    if (this.selectedGroupId) body.groupId = this.selectedGroupId;

    return this.http.post<CustomFormResponse>(`${this.apiUrl}/custom-forms`, body, {
      headers: this.authHeaders(),
    });
  }

  updateForm(id: string, metadata: CustomFormMetadata): Observable<CustomFormResponse> {
    return this.http.put<CustomFormResponse>(`${this.apiUrl}/custom-forms/${id}`,
      this.metadataBody(metadata), { headers: this.authHeaders() });
  }

  /**
   * Soft delete. 409 `CUSTOM_FORM_HAS_OPEN_REQUESTS` carries `details.openRequestCount` and must be
   * shown, not swallowed — patients are mid-form and the practice has to revoke or archive instead.
   */
  deleteForm(id: string): Observable<CustomFormDeleteResponse> {
    return this.http.delete<CustomFormDeleteResponse>(`${this.apiUrl}/custom-forms/${id}`, {
      headers: this.authHeaders(),
    });
  }

  /* ── Versions ───────────────────────────────────────────────────────────────────────────── */

  /**
   * PUBLISH. The only endpoint that accepts a schema, and the only way a form's questions change.
   *
   * 400 `SCHEMA_INVALID` carries `details.errors` — the server is the authority even when the
   * client-side `validateSchema` passed.
   */
  publishVersion(id: string, schema: FormSchema): Observable<PublishVersionResponse> {
    return this.http.post<PublishVersionResponse>(`${this.apiUrl}/custom-forms/${id}/versions`,
      { schema }, { headers: this.authHeaders() });
  }

  /** History, newest first. WITHOUT schemas — §14.1 decision 2. */
  listVersions(id: string): Observable<CustomFormVersionListResponse> {
    return this.http.get<CustomFormVersionListResponse>(`${this.apiUrl}/custom-forms/${id}/versions`, {
      headers: this.authHeaders(),
    });
  }

  /** One immutable version, WITH its schema. There is no PUT and no DELETE beside this. */
  getVersion(versionId: string): Observable<CustomFormVersionResponse> {
    return this.http.get<CustomFormVersionResponse>(`${this.apiUrl}/custom-form-versions/${versionId}`, {
      headers: this.authHeaders(),
    });
  }

  /* ── Appointment-type triggers ──────────────────────────────────────────────────────────── */

  listAppointmentTypeLinks(id: string): Observable<CustomFormLinkListResponse> {
    return this.http.get<CustomFormLinkListResponse>(
      `${this.apiUrl}/custom-forms/${id}/appointment-types`, { headers: this.authHeaders() });
  }

  linkAppointmentType(id: string, appointmentTypeId: string, sendOn: CustomFormSendOn)
    : Observable<CustomFormLinkResponse> {
    return this.http.post<CustomFormLinkResponse>(
      `${this.apiUrl}/custom-forms/${id}/appointment-types`,
      { appointmentTypeId, sendOn },
      { headers: this.authHeaders() });
  }

  /**
   * §14.1 decision 1 — `sendOn` is REQUIRED here, not optional as the endpoint allows.
   *
   * The endpoint's `?sendOn=` is optional and its absence removes BOTH triggers for the pair. This
   * UI lists booking and reminder as separate rows, so an unlink of one row that silently removed
   * the other would delete configuration the practice never touched. The parameter is therefore
   * mandatory at this boundary; the widening behaviour is reachable only by choosing it, which no
   * caller in this feature does.
   */
  unlinkAppointmentType(id: string, appointmentTypeId: string, sendOn: CustomFormSendOn)
    : Observable<CustomFormUnlinkResponse> {
    return this.http.delete<CustomFormUnlinkResponse>(
      `${this.apiUrl}/custom-forms/${id}/appointment-types/${appointmentTypeId}`,
      { headers: this.authHeaders(), params: new HttpParams().set('sendOn', sendOn) });
  }

  /* ── AI import from a PDF (§16.3) ───────────────────────────────────────────────────────── */

  /**
   * UPLOAD — `POST /custom-forms/:id/imports`, multipart.
   *
   * The response is `201 { import }` with `status: 'pending'`: the vision call has not run yet and
   * will not for tens of seconds. Nothing about this call produces questions — it produces a JOB,
   * and §16.1 exists precisely so the job survives the tab closing.
   *
   * **No `Content-Type` is set.** `authHeaders()` carries the token and nothing else, so the browser
   * writes `multipart/form-data` with its own boundary. Setting the header by hand is the standard
   * way to break a multipart upload: the boundary would be missing and the server would parse no
   * file at all.
   */
  startImport(id: string, file: File): Observable<CustomFormImportResponse> {
    const body = new FormData();
    // `app.js` mounts `upload.any()` and the controllers read `req.files[0]`, so the field name is
    // not load-bearing on the server — it is named `file` because that is what it is.
    body.append('file', file, file.name);

    return this.http.post<CustomFormImportResponse>(
      `${this.apiUrl}/custom-forms/${id}/imports`, body, { headers: this.authHeaders() });
  }

  /** History for one form, newest first. §16: a failed import must be visible, not silently gone. */
  listImports(id: string): Observable<CustomFormImportListResponse> {
    return this.http.get<CustomFormImportListResponse>(
      `${this.apiUrl}/custom-forms/${id}/imports`, { headers: this.authHeaders() });
  }

  /**
   * One import, WITH its schema once it is `completed`.
   *
   * This is what the waiting UI polls and what the socket events tell it to re-read: the events go
   * to the group room and cannot carry an encrypted draft schema, so they are a nudge and this is
   * the truth.
   */
  getImport(importId: string): Observable<CustomFormImportResponse> {
    return this.http.get<CustomFormImportResponse>(
      `${this.apiUrl}/custom-form-imports/${importId}`, { headers: this.authHeaders() });
  }

  /* ── Helpers ────────────────────────────────────────────────────────────────────────────── */

  /**
   * The stored `validateSchema` errors of an import, however they arrived.
   *
   * A dedicated array if the endpoint ever grows one; otherwise the bullet list the worker folds
   * into `errorMessage` when the extraction is stored-but-invalid (§16.2 rule 2). That fallback is
   * a parse of the server's own prose and it is written down as one — it is contained here, it
   * degrades to an empty list, and the whole `errorMessage` is displayed by the UI regardless, so
   * a change of wording on the server costs the per-question attribution and nothing else.
   */
  static importSchemaErrors(dto: CustomFormImportDto | null | undefined): string[] {
    if (!dto) return [];

    const declared = dto.schemaErrors || dto.validationErrors;
    if (Array.isArray(declared)) {
      return declared.filter((error) => typeof error === 'string' && error.trim() !== '');
    }

    const message = dto.errorMessage;
    if (typeof message !== 'string' || message.indexOf('\n- ') === -1) return [];
    return message
      .split('\n')
      .filter((line) => line.indexOf('- ') === 0)
      .map((line) => line.slice(2).trim())
      .filter((line) => line !== '');
  }

  /**
   * "Read, but it needs repair before it can be published."
   *
   * §16.2 rule 2 makes a schema `validateSchema` refuses a FAILED import that nevertheless KEEPS
   * its draft — the extraction is already paid for, and repairing it in the builder beats running
   * it again. So "can this be reviewed?" is not "is it completed?", it is "is there a schema", and
   * a UI that asked the first question would throw away exactly the case rule 2 exists for.
   */
  static hasRepairableSchema(dto: CustomFormImportDto | null | undefined): boolean {
    if (!dto) return false;
    if (dto.schema && Array.isArray(dto.schema.sections) && dto.schema.sections.length > 0) {
      return true;
    }
    return dto.hasSchema === true;
  }

  /**
   * The server's refusal, or null for anything that is not one (a network drop, a 502 from a proxy,
   * a body that is not JSON). Callers show `message` and branch on `error`.
   */
  static asApiError(error: any): CustomFormApiError | null {
    const body = error instanceof HttpErrorResponse ? error.error : (error && error.error);
    if (body && typeof body === 'object' && typeof body.error === 'string') {
      return body as CustomFormApiError;
    }
    return null;
  }

  /**
   * FIELD BY FIELD, never a spread.
   *
   * A spread of a component's form value would carry `id`, `groupId`, `currentVersionId` and — the
   * one that matters — `schema`, each of which the controller refuses by name. `undefined` fields
   * are omitted so a PUT touches only what the user edited; `null` is kept, because for
   */
  private metadataBody(metadata: CustomFormMetadata): any {
    const body: any = {};
    const keys: (keyof CustomFormMetadata)[] = [
      'name', 'description', 'category', 'status', 'sendWithInvite', 'allowManualSend',
      'requiresSignature', 'guardianMayComplete', 'expiryDays', 'active',
    ];
    for (const key of keys) {
      if (metadata[key] !== undefined) body[key] = metadata[key];
    }
    return body;
  }

  private authHeaders(): { [header: string]: string } {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }
}
